import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  AddRounded,
  ArrowBackRounded,
  ArrowForwardRounded,
  AssignmentTurnedInRounded,
  AutoAwesomeRounded,
  BalanceRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  ChevronRightRounded,
  DescriptionRounded,
  EditRounded,
  EventAvailableRounded,
  FactCheckRounded,
  FolderSpecialRounded,
  GavelRounded,
  HistoryRounded,
  LockRounded,
  OpenInNewRounded,
  PlayArrowRounded,
  SearchRounded,
  ShieldRounded,
  TaskAltRounded,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  approveRecord,
  createMatterRecord,
  getMatterPlaybook,
  listMatters,
  updateMatter,
  updateTask,
} from '../features/matters/api';
import { ACTIVE_MATTER_KEY, COPILOT_PROFILE_KEY, COPILOT_PREFILL_KEY } from '../edition';

const STATUSES = ['not-started', 'in-progress', 'blocked', 'completed', 'not-applicable'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const STATUS_LABELS = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  blocked: 'Blocked',
  completed: 'Completed',
  'not-applicable': 'Not applicable',
};

const TOOL_META = {
  intake: { label: 'Client intake', description: 'Conflict, identity, retainer, and intake controls', icon: <AssignmentTurnedInRounded /> },
  matter: { label: 'Matter record', description: 'Verified profile, dates, objectives, and ownership', icon: <FolderSpecialRounded /> },
  evidence: { label: 'Evidence register', description: 'Preserved originals, disclosure, productions, and derivatives', icon: <DescriptionRounded /> },
  facts: { label: 'Facts & chronology', description: 'Source-linked facts, disputes, and events', icon: <FactCheckRounded /> },
  issues: { label: 'Issues & research plan', description: 'Particularized questions and research state', icon: <BalanceRounded /> },
  research: { label: 'Case-law intelligence', description: 'Issues, claims, charges, remedies, outcomes, and authorities', icon: <SearchRounded /> },
  drafting: { label: 'Controlled drafting', description: 'Live editing, review gates, and version history', icon: <GavelRounded /> },
  copilot: { label: 'Matter copilot', description: 'Context-aware work with private matter grounding', icon: <AutoAwesomeRounded /> },
  audit: { label: 'Audit trail', description: 'Approvals, activity, and defensible provenance', icon: <HistoryRounded /> },
};

function formatDate(value, includeTime = false) {
  if (!value) return 'Not set';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-CA', includeTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

function statusTone(value) {
  if (value === 'completed' || value === 'complete' || value === 'approved') return 'success';
  if (value === 'blocked' || value === 'at-risk' || value === 'overdue' || value === 'critical') return 'error';
  if (value === 'in-progress' || value === 'warning' || value === 'due-soon') return 'warning';
  if (value === 'current' || value === 'info') return 'info';
  return 'default';
}

function stageStateLabel(value) {
  if (value === 'at-risk') return 'At risk';
  if (value === 'prior') return 'Prior stage';
  if (value === 'upcoming') return 'Upcoming';
  if (value === 'complete') return 'Complete';
  return value === 'current' ? 'Current stage' : 'Stage';
}

function taskComplete(task) {
  return task.status === 'completed' || task.status === 'not-applicable';
}

function taskDraft(task, stage) {
  return task ? {
    id: task.id,
    title: task.title,
    status: task.status,
    priority: task.priority,
    assignedTo: task.assignedTo || '',
    dueDate: task.dueDate || '',
    notes: task.notes || '',
    requiresApproval: task.requiresApproval,
  } : {
    title: '',
    status: 'not-started',
    priority: 'normal',
    assignedTo: '',
    dueDate: '',
    notes: '',
    requiresApproval: false,
    stage: stage?.id || '',
    stageTitle: stage?.title || '',
  };
}

function MetricCard({ icon, label, value, detail, color = '#245B88' }) {
  return (
    <Card sx={{ p: 1.8, minWidth: 0 }}>
      <Stack direction="row" spacing={1.35} alignItems="center">
        <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: 2.3, display: 'grid', placeItems: 'center', color, bgcolor: alpha(color, 0.09) }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, lineHeight: 1.08, fontWeight: 800 }}>{value}</Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.primary' }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{detail}</Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function EmptyMatter({ onStart }) {
  return (
    <Paper variant="outlined" sx={{ maxWidth: 760, mx: 'auto', mt: 8, p: { xs: 3, md: 6 }, textAlign: 'center', borderStyle: 'dashed' }}>
      <Avatar sx={{ width: 58, height: 58, mx: 'auto', bgcolor: 'secondary.main' }}><AssignmentTurnedInRounded /></Avatar>
      <Typography variant="h4" sx={{ mt: 2 }}>Open a matter before running a playbook</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>The lifecycle is generated from a conflict-cleared intake and stays connected to the matter record, evidence, research, drafting, approvals, and audit history.</Typography>
      <Button variant="contained" startIcon={<AddRounded />} onClick={onStart}>Start client intake</Button>
    </Paper>
  );
}

export function StageRail({ stages, selectedId, currentStageId, onSelect, onOpenStage }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', position: { xl: 'sticky' }, top: { xl: 0 }, alignSelf: 'start' }}>
      <Box sx={{ px: 2, py: 1.6, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary">Matter lifecycle</Typography>
        <Typography variant="body2">Open a stage to see its work, outputs, and next action.</Typography>
      </Box>
      <Box component="nav" aria-label="Matter lifecycle stages" sx={{ p: 0.75 }}>
        {stages.map((stage, index) => {
          const selected = stage.id === selectedId;
          const current = stage.id === currentStageId;
          const nextTask = stage.tasks?.find(task => !task.effectivelyComplete);
          const detailId = `lifecycle-stage-${stage.id}`;
          return (
            <Box key={stage.id} sx={{ mb: 0.35 }}>
              <Box
                component="button"
                type="button"
                aria-expanded={selected}
                aria-controls={detailId}
                onClick={() => onSelect(stage.id)}
                sx={{
                  appearance: 'none', width: '100%', border: 0, textAlign: 'left', cursor: 'pointer',
                  px: 1.25, py: 1.15, borderRadius: selected ? '10px 10px 4px 4px' : 2,
                  bgcolor: selected ? 'rgba(13,148,136,.10)' : 'transparent', color: 'text.primary',
                  '&:hover': { bgcolor: selected ? 'rgba(13,148,136,.14)' : 'action.hover' },
                  '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.light', outlineOffset: 1 },
                }}
              >
                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                  <Box sx={{ width: 26, height: 26, mt: 0.1, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: stage.state === 'complete' ? '#fff' : selected ? 'secondary.dark' : 'text.secondary', bgcolor: stage.state === 'complete' ? 'success.main' : selected ? 'secondary.light' : 'action.selected', border: '1px solid', borderColor: stage.state === 'at-risk' ? 'error.main' : 'transparent' }}>
                    {stage.state === 'complete' ? <CheckCircleRounded sx={{ fontSize: 17 }} /> : index + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.7} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: selected ? 800 : 650, flex: 1 }}>{stage.shortTitle}</Typography>
                      {current && <Chip size="small" label="Current" color="secondary" sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }} />}
                      {stage.summary.overdue > 0 && <Chip size="small" color="error" label={stage.summary.overdue} sx={{ height: 20, minWidth: 25 }} />}
                      <ChevronRightRounded sx={{ fontSize: 19, color: selected ? 'secondary.main' : 'text.disabled', transform: selected ? 'rotate(90deg)' : 'none', transition: 'transform 180ms ease' }} />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.65 }}>
                      <LinearProgress variant="determinate" value={stage.summary.progress} color={stage.state === 'at-risk' ? 'error' : 'secondary'} sx={{ flex: 1, height: 4.5, borderRadius: 5 }} />
                      <Typography variant="caption" color="text.secondary">{stage.summary.controlledComplete}/{stage.summary.total}</Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              <Collapse in={selected} timeout={180} unmountOnExit>
                <Box id={detailId} sx={{ mx: 0.5, px: 1.35, pt: 1.15, pb: 1.35, borderRadius: '3px 3px 10px 10px', bgcolor: 'rgba(13,148,136,.055)', border: '1px solid', borderTop: 0, borderColor: 'rgba(13,148,136,.22)' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>{stage.goal}</Typography>
                  <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip size="small" label={`${Math.max(0, stage.summary.total - stage.summary.controlledComplete)} open`} variant="outlined" />
                    {stage.summary.awaitingApproval > 0 && <Chip size="small" label={`${stage.summary.awaitingApproval} approval`} color="warning" variant="outlined" />}
                    {stage.summary.blocked > 0 && <Chip size="small" label={`${stage.summary.blocked} blocked`} color="error" variant="outlined" />}
                  </Stack>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.15, lineHeight: 1.6 }}>Next stage action</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 720, lineHeight: 1.4 }}>{nextTask?.title || 'All stage controls are complete'}</Typography>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.15, lineHeight: 1.6 }}>Key outputs</Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.45 }}>{stage.deliverables?.slice(0, 3).join(' · ') || 'No configured outputs'}</Typography>
                  <Button fullWidth size="small" variant="outlined" aria-label={`Open ${stage.shortTitle} stage workspace`} onClick={() => onOpenStage(stage.id)} endIcon={<ArrowForwardRounded />} sx={{ mt: 1.25, justifyContent: 'space-between' }}>Open stage workspace</Button>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function TaskRow({ task, onToggle, onStart, onEdit, onApprove, busy }) {
  const awaitingApproval = task.status === 'completed' && task.requiresApproval && !task.approvedAt;
  return (
    <Paper variant="outlined" sx={{ p: 1.35, borderColor: task.dueState === 'overdue' || task.status === 'blocked' ? 'error.light' : 'divider', bgcolor: task.status === 'not-applicable' ? 'action.hover' : 'background.paper' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Checkbox
          checked={taskComplete(task)}
          indeterminate={task.status === 'in-progress'}
          disabled={busy || task.status === 'not-applicable'}
          onChange={() => onToggle(task)}
          inputProps={{ 'aria-label': `${taskComplete(task) ? 'Reopen' : 'Complete'} ${task.title}` }}
          sx={{ mt: -0.7, ml: -0.7 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 720, textDecoration: task.status === 'not-applicable' ? 'line-through' : 'none', color: task.status === 'not-applicable' ? 'text.secondary' : 'text.primary' }}>{task.title}</Typography>
          <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
            <Chip size="small" label={STATUS_LABELS[task.status] || task.status} color={statusTone(task.status)} variant={task.status === 'not-started' ? 'outlined' : 'filled'} />
            <Chip size="small" label={task.priority} color={task.priority === 'urgent' ? 'error' : task.priority === 'high' ? 'warning' : 'default'} variant="outlined" />
            {task.dueDate && <Chip size="small" icon={<CalendarMonthRounded />} label={`${task.dueState === 'overdue' ? 'Overdue · ' : ''}${formatDate(task.dueDate)}`} color={statusTone(task.dueState)} variant="outlined" />}
            {task.assignedTo && <Chip size="small" label={task.assignedTo} variant="outlined" />}
            {task.requiresApproval && <Chip size="small" icon={<ShieldRounded />} label={task.approvedAt ? `Approved · ${task.approvedBy}` : 'Approval required'} color={task.approvedAt ? 'success' : awaitingApproval ? 'warning' : 'default'} variant="outlined" />}
          </Stack>
          {task.notes && <Typography variant="caption" color={task.status === 'blocked' ? 'error.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.8 }}>{task.notes}</Typography>}
          {(task.completedAt || task.approvedAt) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.65 }}>{task.completedAt ? `Completed ${formatDate(task.completedAt, true)}${task.completedBy ? ` by ${task.completedBy}` : ''}` : ''}{task.completedAt && task.approvedAt ? ' · ' : ''}{task.approvedAt ? `Approved ${formatDate(task.approvedAt, true)}` : ''}</Typography>}
        </Box>
        <Stack direction="row" spacing={0.6} justifyContent="flex-end">
          {task.status === 'not-started' && <Button size="small" variant="text" startIcon={<PlayArrowRounded />} onClick={() => onStart(task)} disabled={busy}>Start</Button>}
          {awaitingApproval && <Button size="small" variant="outlined" color="warning" onClick={() => onApprove(task)}>Review</Button>}
          <Tooltip title="Edit task controls"><IconButton size="small" onClick={() => onEdit(task)}><EditRounded fontSize="small" /></IconButton></Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

function TaskDialog({ open, value, setValue, onClose, onSave, busy }) {
  const field = key => event => setValue(current => ({ ...current, [key]: event.target.value }));
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{value.id ? 'Edit workflow task' : 'Add matter task'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField label="Task" value={value.title || ''} onChange={field('title')} fullWidth autoFocus />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField select label="Status" value={value.status || 'not-started'} onChange={field('status')} fullWidth>
              {STATUSES.map(status => <MenuItem key={status} value={status}>{STATUS_LABELS[status]}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={value.priority || 'normal'} onChange={field('priority')} fullWidth>
              {PRIORITIES.map(priority => <MenuItem key={priority} value={priority}>{priority[0].toUpperCase() + priority.slice(1)}</MenuItem>)}
            </TextField>
            <TextField label="Due date" type="date" value={value.dueDate || ''} onChange={field('dueDate')} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Responsible professional" value={value.assignedTo || ''} onChange={field('assignedTo')} placeholder="Unassigned" fullWidth />
          </Box>
          <TextField label={value.status === 'blocked' ? 'Blocker and escalation plan' : value.status === 'not-applicable' ? 'Reason this task is not applicable' : 'Working notes'} value={value.notes || ''} onChange={field('notes')} multiline minRows={3} helperText={value.status === 'not-applicable' ? 'A reason is required for defensible scope control.' : 'Do not put privileged detail here unless it belongs in the matter record.'} fullWidth />
          <FormControlLabel control={<Switch checked={Boolean(value.requiresApproval)} onChange={event => setValue(current => ({ ...current, requiresApproval: event.target.checked }))} />} label="Require independent approval before controlled completion" />
          {value.approvedAt && <Alert severity="warning">Changing a controlled field invalidates the existing approval and returns this item to review.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={busy || !value.title?.trim() || (value.status === 'not-applicable' && !value.notes?.trim())}>{busy ? 'Saving…' : 'Save task'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApprovalDialog({ task, value, setValue, onClose, onSave, busy }) {
  return (
    <Dialog open={Boolean(task)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Professional review decision</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontWeight: 740 }}>{task?.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>The decision and note become part of the matter audit trail.</Typography>
        <Stack spacing={2}>
          <TextField select label="Decision" value={value.decision} onChange={event => setValue(current => ({ ...current, decision: event.target.value }))} fullWidth>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="changes-requested">Changes requested</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField label="Review note" value={value.note} onChange={event => setValue(current => ({ ...current, note: event.target.value }))} multiline minRows={3} placeholder="Record what was checked and any limits on the decision." helperText="Required for an auditable review decision." fullWidth autoFocus />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={busy || value.note.trim().length < 8}>{busy ? 'Recording…' : 'Record decision'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Steps() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSelection = useRef(searchParams.get('matterId') || searchParams.get('id') || localStorage.getItem(ACTIVE_MATTER_KEY) || '');
  const initialStage = useRef(searchParams.get('stage') || '');
  const [matters, setMatters] = useState([]);
  const [playbook, setPlaybook] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [taskSearch, setTaskSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [taskEditor, setTaskEditor] = useState(null);
  const [approvalTask, setApprovalTask] = useState(null);
  const [approval, setApproval] = useState({ decision: 'approved', note: '' });
  const stageWorkspaceRef = useRef(null);

  const writeMatterContext = useCallback(matter => {
    if (!matter) return;
    localStorage.setItem(ACTIVE_MATTER_KEY, matter.id);
    localStorage.setItem(COPILOT_PROFILE_KEY, JSON.stringify({
      matterId: matter.id,
      matterName: matter.title,
      clientName: matter.clientName,
      jurisdiction: matter.jurisdiction,
      court: matter.court,
      currentStage: matter.stage,
      objectives: matter.objectives,
      nextCourtDate: matter.nextCourtDate || '',
      lawyerPreferences: 'Separate verified facts, disputed facts, assumptions, supporting authorities, adverse authorities, deadlines, and decisions requiring human approval.',
    }));
  }, []);

  const loadPlaybook = useCallback(async (matterId, resetStage = false, preferredStage = '') => {
    const body = await getMatterPlaybook(matterId);
    setPlaybook(body.playbook);
    writeMatterContext(body.playbook.matter);
    setSelectedStageId(current => {
      if (preferredStage && body.playbook.stages.some(stage => stage.id === preferredStage)) return preferredStage;
      if (!resetStage && body.playbook.stages.some(stage => stage.id === current)) return current;
      return body.playbook.matter.stage || body.playbook.stages[0]?.id || '';
    });
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('matterId', matterId);
      next.delete('id');
      return next;
    }, { replace: true });
    return body.playbook;
  }, [setSearchParams, writeMatterContext]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const body = await listMatters();
        if (!active) return;
        const available = body.matters || [];
        setMatters(available);
        const preferred = available.find(matter => matter.id === initialSelection.current) || available[0];
        if (preferred) await loadPlaybook(preferred.id, true, initialStage.current);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // The URL/local-storage selection is intentionally captured once at page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStage = useMemo(() => playbook?.stages.find(stage => stage.id === selectedStageId) || playbook?.stages[0] || null, [playbook, selectedStageId]);
  const selectedStageIndex = useMemo(() => playbook?.stages.findIndex(stage => stage.id === selectedStage?.id) ?? -1, [playbook, selectedStage]);
  const previousStage = selectedStageIndex > 0 ? playbook?.stages[selectedStageIndex - 1] : null;
  const nextStage = selectedStageIndex >= 0 ? playbook?.stages[selectedStageIndex + 1] : null;
  const stageDocuments = useMemo(() => (playbook?.documents || []).filter(document => document.metadata?.lifecycleStageId === selectedStage?.id), [playbook, selectedStage]);
  const stageOpenTasks = useMemo(() => (selectedStage?.tasks || []).filter(task => !task.effectivelyComplete), [selectedStage]);
  const stageInProgress = useMemo(() => (selectedStage?.tasks || []).filter(task => task.status === 'in-progress').length, [selectedStage]);
  const stageUnassigned = useMemo(() => stageOpenTasks.filter(task => !task.assignedTo).length, [stageOpenTasks]);
  const visibleTasks = useMemo(() => {
    const query = taskSearch.trim().toLowerCase();
    return (selectedStage?.tasks || []).filter(task => {
      if (statusFilter === 'open' && task.effectivelyComplete) return false;
      if (statusFilter === 'completed' && !task.effectivelyComplete) return false;
      if (statusFilter === 'attention' && !['blocked'].includes(task.status) && task.dueState !== 'overdue' && !(task.status === 'completed' && task.requiresApproval && !task.approvedAt)) return false;
      if (statusFilter === 'in-progress' && task.status !== 'in-progress') return false;
      if (statusFilter === 'approval' && !(task.status === 'completed' && task.requiresApproval && !task.approvedAt)) return false;
      if (statusFilter === 'unassigned' && (task.effectivelyComplete || task.assignedTo)) return false;
      return !query || `${task.title} ${task.notes} ${task.assignedTo}`.toLowerCase().includes(query);
    });
  }, [selectedStage, statusFilter, taskSearch]);

  function toolRoute(toolId) {
    const matterId = playbook?.matter.id || '';
    const encoded = encodeURIComponent(matterId);
    const routes = {
      intake: '/intake',
      matter: `/matters?id=${encoded}&view=overview`,
      evidence: `/matters?id=${encoded}&view=evidence`,
      facts: `/matters?id=${encoded}&view=record`,
      issues: `/matters?id=${encoded}&view=issues`,
      audit: `/matters?id=${encoded}&view=audit`,
      drafting: `/drafting?matterId=${encoded}&stage=${encodeURIComponent(selectedStage?.id || playbook?.matter.stage || '')}`,
      research: '/research?view=patterns',
      copilot: '/copilot',
    };
    return routes[toolId] || `/matters?id=${encoded}`;
  }

  function openTool(toolId, prefill = '') {
    writeMatterContext(playbook?.matter);
    if (toolId === 'copilot' && prefill) localStorage.setItem(COPILOT_PREFILL_KEY, prefill);
    navigate(toolRoute(toolId));
  }

  function openDocument(documentId) {
    navigate(`/drafting?matterId=${encodeURIComponent(playbook?.matter.id || '')}&documentId=${encodeURIComponent(documentId)}`);
  }

  async function mutate(action, successMessage) {
    if (!playbook) return;
    setBusy(true);
    setError('');
    try {
      await action();
      await loadPlaybook(playbook.matter.id);
      if (successMessage) setSnackbar(successMessage);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleTask(task) {
    const next = taskComplete(task) ? 'not-started' : 'completed';
    await mutate(() => updateTask(playbook.matter.id, task.id, { status: next }), next === 'completed' && task.requiresApproval ? 'Work completed and queued for approval.' : 'Task status updated.');
  }

  async function startTask(task) {
    await mutate(() => updateTask(playbook.matter.id, task.id, { status: 'in-progress' }), 'Task started. Assign an owner and due date if they are not already set.');
  }

  async function saveTask() {
    const payload = { ...taskEditor };
    delete payload.id;
    await mutate(
      () => taskEditor.id
        ? updateTask(playbook.matter.id, taskEditor.id, payload)
        : createMatterRecord(playbook.matter.id, 'tasks', { ...payload, stage: selectedStage.id, stageTitle: selectedStage.title, sortOrder: selectedStage.index * 100 + 90, source: 'matter-specific' }),
      taskEditor.id ? 'Task controls saved.' : 'Matter-specific task added.',
    );
    setTaskEditor(null);
  }

  async function saveApproval() {
    await mutate(() => approveRecord(playbook.matter.id, { entityType: 'workflow_task', entityId: approvalTask.id, ...approval }), 'Review decision recorded in the audit trail.');
    setApprovalTask(null);
    setApproval({ decision: 'approved', note: '' });
  }

  async function makeCurrentStage() {
    await mutate(() => updateMatter(playbook.matter.id, { stage: selectedStage.id }), `${selectedStage.shortTitle} is now the current matter stage.`);
  }

  function focusStageWorkspace() {
    window.setTimeout(() => {
      stageWorkspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      stageWorkspaceRef.current?.focus({ preventScroll: true });
    }, 80);
  }

  function chooseStage(stageId, focusWorkspace = false) {
    setSelectedStageId(stageId);
    setTaskSearch('');
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('stage', stageId);
      return next;
    }, { replace: true });
    if (focusWorkspace) focusStageWorkspace();
  }

  if (loading && !playbook) return <Box sx={{ minHeight: 440, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!matters.length && !loading) return <EmptyMatter onStart={() => navigate('/intake')} />;

  const controls = playbook?.controls || {};
  const readiness = playbook?.readiness || {};
  const currentStage = playbook?.stages[playbook.currentStageIndex];

  return (
    <Box sx={{ maxWidth: 1740, mx: 'auto', pb: 5 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between" sx={{ mb: 2.2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="h3">Practice playbook</Typography>
            <Chip size="small" icon={<ShieldRounded />} label={`Controlled workflow · v${playbook?.version || 1}`} color="secondary" variant="outlined" />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.45 }}>One operational plan for the matter record, evidence, research, drafting, approvals, and court work.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Select
            size="small"
            value={playbook?.matter.id || ''}
            onChange={event => { setLoading(true); loadPlaybook(event.target.value, true).catch(err => setError(err.message)).finally(() => setLoading(false)); }}
            inputProps={{ 'aria-label': 'Select matter' }}
            sx={{ minWidth: { sm: 330 } }}
          >
            {matters.map(matter => <MenuItem key={matter.id} value={matter.id}>{matter.matterNumber} · {matter.title}</MenuItem>)}
          </Select>
          <Button variant="outlined" startIcon={<FolderSpecialRounded />} onClick={() => openTool('matter')}>Open matter</Button>
          <Button variant="contained" startIcon={<AutoAwesomeRounded />} onClick={() => openTool('copilot', `Review the current ${currentStage?.title || 'matter'} stage for ${playbook?.matter.title}. Identify missing information, approaching deadlines, blockers, approval gates, and the next three actions. Do not invent facts or authorities.`)}>Ask copilot</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: { xs: 2, md: 2.4 }, mb: 2, color: '#fff', background: 'linear-gradient(120deg, #0B2135 0%, #153C5A 68%, #126D68 140%)', overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(73,206,190,.08)', right: -70, top: -125 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ position: 'relative' }}>
          <Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <Typography variant="h4" color="inherit">{playbook?.matter.title}</Typography>
              <Chip size="small" label={playbook?.matter.status} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.12)' }} />
            </Stack>
            <Typography sx={{ mt: 0.55, color: 'rgba(255,255,255,.72)' }}>{playbook?.matter.matterNumber} · {playbook?.matter.clientName || 'Client not set'} · {playbook?.matter.jurisdiction || 'Jurisdiction not set'} · {playbook?.matter.court || 'Court not set'}</Typography>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.3, color: 'rgba(255,255,255,.78)' }}>
              <Typography variant="caption"><b>Current stage:</b> {currentStage?.shortTitle || 'Not set'}</Typography>
              <Typography variant="caption"><b>Responsible:</b> {playbook?.matter.assignedTo || 'Unassigned'}</Typography>
              <Typography variant="caption"><b>Next court date:</b> {formatDate(playbook?.matter.nextCourtDate)}</Typography>
            </Stack>
          </Box>
          <Box sx={{ width: { xs: '100%', md: 280 } }}>
            <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Controlled completion</Typography><Typography variant="caption" fontWeight={800}>{controls.progress || 0}%</Typography></Stack>
            <LinearProgress variant="determinate" value={controls.progress || 0} color="secondary" sx={{ mt: 0.8, height: 8, borderRadius: 6, bgcolor: 'rgba(255,255,255,.12)' }} />
            <Typography variant="caption" sx={{ mt: 0.7, display: 'block', color: 'rgba(255,255,255,.62)' }}>{controls.controlledComplete || 0} of {controls.totalTasks || 0} steps complete after required approvals</Typography>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.35, mb: 2 }}>
        <MetricCard icon={<TaskAltRounded />} label="Controlled completion" value={`${controls.progress || 0}%`} detail={`${controls.rawComplete || 0} marked done · ${controls.controlledComplete || 0} controlled`} />
        <MetricCard icon={<EventAvailableRounded />} label="Deadline watch" value={(controls.overdue || 0) + (controls.dueSoon || 0)} detail={`${controls.overdue || 0} overdue · ${controls.dueSoon || 0} due in 7 days`} color="#C2414B" />
        <MetricCard icon={<ShieldRounded />} label="Approval queue" value={controls.awaitingApproval || 0} detail={`${controls.blocked || 0} blocked task${controls.blocked === 1 ? '' : 's'}`} color="#D97706" />
        <MetricCard icon={<FactCheckRounded />} label="Profile readiness" value={`${readiness.profile?.percent || 0}%`} detail={`${readiness.facts?.sourced || 0}/${readiness.facts?.total || 0} facts fully sourced`} color="#0D9488" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '320px minmax(0, 1fr) 315px' }, gap: 2, alignItems: 'start' }}>
        <StageRail
          stages={playbook?.stages || []}
          selectedId={selectedStage?.id}
          currentStageId={playbook?.matter.stage}
          onSelect={chooseStage}
          onOpenStage={stageId => { chooseStage(stageId); focusStageWorkspace(); }}
        />

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Paper ref={stageWorkspaceRef} id={`stage-workspace-${selectedStage?.id || 'selected'}`} tabIndex={-1} variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, scrollMarginTop: 18, outline: 'none', borderColor: selectedStage?.state === 'at-risk' ? 'error.light' : 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="overline" color="text.secondary">Stage {selectedStageIndex + 1} of {playbook?.stages.length || 0} · Command centre</Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={previousStage ? `Previous: ${previousStage.shortTitle}` : 'First lifecycle stage'}>
                  <span><IconButton size="small" disabled={!previousStage} onClick={() => chooseStage(previousStage.id)} aria-label="Open previous lifecycle stage"><ArrowBackRounded fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title={nextStage ? `Next: ${nextStage.shortTitle}` : 'Final lifecycle stage'}>
                  <span><IconButton size="small" disabled={!nextStage} onClick={() => chooseStage(nextStage.id)} aria-label="Open next lifecycle stage"><ArrowForwardRounded fontSize="small" /></IconButton></span>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography variant="h4">{selectedStage?.title}</Typography>
                  <Chip size="small" label={stageStateLabel(selectedStage?.state)} color={statusTone(selectedStage?.state)} variant="outlined" />
                </Stack>
                <Typography color="text.secondary" sx={{ mt: 0.65 }}>{selectedStage?.goal}</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={0.8} sx={{ width: { xs: '100%', md: 'auto' } }}>
                {selectedStage?.id !== playbook?.matter.stage && <Button variant="outlined" startIcon={<PlayArrowRounded />} onClick={makeCurrentStage} disabled={busy}>Set current stage</Button>}
                <Button variant="contained" startIcon={<DescriptionRounded />} onClick={() => openTool('drafting')}>Create stage document</Button>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={selectedStage?.summary.progress || 0} color={selectedStage?.state === 'at-risk' ? 'error' : 'secondary'} sx={{ flex: 1, height: 7, borderRadius: 8 }} />
              <Typography variant="body2" fontWeight={760}>{selectedStage?.summary.progress || 0}%</Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, gap: 1, mt: 1.6 }}>
              {[
                { label: 'Open work', value: stageOpenTasks.length, detail: `${selectedStage?.summary.controlledComplete || 0} controlled complete` },
                { label: 'In progress', value: stageInProgress, detail: `${stageUnassigned} open unassigned` },
                { label: 'Approval queue', value: selectedStage?.summary.awaitingApproval || 0, detail: `${selectedStage?.summary.blocked || 0} blocked` },
                { label: 'Stage drafts', value: stageDocuments.length, detail: `${selectedStage?.deliverables.length || 0} required outputs` },
              ].map(metric => (
                <Box key={metric.label} sx={{ p: 1.15, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 820, lineHeight: 1.1 }}>{metric.value}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 720 }}>{metric.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{metric.detail}</Typography>
                </Box>
              ))}
            </Box>
            {(selectedStage?.summary.overdue > 0 || selectedStage?.summary.blocked > 0) && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                This stage has {selectedStage.summary.overdue || 0} overdue and {selectedStage.summary.blocked || 0} blocked item{(selectedStage.summary.overdue || 0) + (selectedStage.summary.blocked || 0) === 1 ? '' : 's'}. Resolve or document the escalation before relying on stage completion.
              </Alert>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.2 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1.6 }}>
              <Box sx={{ flex: 1 }}><Typography variant="h5">Stage work</Typography><Typography variant="caption" color="text.secondary">Ownership, deadlines, status, notes, and independent approval are shared across the matter.</Typography></Box>
              <TextField size="small" value={taskSearch} onChange={event => setTaskSearch(event.target.value)} placeholder="Find a task" InputProps={{ startAdornment: <SearchRounded sx={{ mr: 0.8, color: 'text.secondary', fontSize: 19 }} /> }} sx={{ minWidth: { md: 180 } }} />
              <Select size="small" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} sx={{ minWidth: 128 }}>
                <MenuItem value="open">Open work</MenuItem><MenuItem value="in-progress">In progress</MenuItem><MenuItem value="attention">Needs attention</MenuItem><MenuItem value="approval">Awaiting approval</MenuItem><MenuItem value="unassigned">Unassigned</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="all">All tasks</MenuItem>
              </Select>
              <Button startIcon={<AddRounded />} onClick={() => setTaskEditor(taskDraft(null, selectedStage))}>Add task</Button>
            </Stack>
            <Stack spacing={1}>
              {visibleTasks.map(task => <TaskRow key={task.id} task={task} busy={busy} onToggle={toggleTask} onStart={startTask} onEdit={item => setTaskEditor({ ...taskDraft(item, selectedStage), approvedAt: item.approvedAt })} onApprove={item => { setApprovalTask(item); setApproval({ decision: 'approved', note: '' }); }} />)}
              {!visibleTasks.length && <Box sx={{ py: 5, textAlign: 'center' }}><CheckCircleRounded color="success" sx={{ fontSize: 38 }} /><Typography variant="h6" sx={{ mt: 0.7 }}>No tasks match this view</Typography><Typography variant="body2" color="text.secondary">Change the filter or add matter-specific work.</Typography></Box>}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">Required work product</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 1.5 }}>Stage deliverables are planning controls. A document only becomes approved or filed after its drafting quality gates pass.</Typography>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">{selectedStage?.deliverables.map(item => <Chip key={item} icon={<DescriptionRounded />} label={item} variant="outlined" />)}</Stack>
                {stageDocuments.length > 0 && (
                  <Box sx={{ mt: 1.7, p: 1.2, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="overline" color="text.secondary">Drafts connected to this stage</Typography>
                    <Stack spacing={0.45} sx={{ mt: 0.25 }}>
                      {stageDocuments.slice(0, 3).map(document => (
                        <Button key={document.id} size="small" variant="text" onClick={() => openDocument(document.id)} endIcon={<OpenInNewRounded />} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                          <Box sx={{ minWidth: 0 }}><Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{document.title}</Typography><Typography variant="caption" color="text.secondary">{document.status} · version {document.versionNumber}</Typography></Box>
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                )}
                <Button sx={{ mt: 2 }} variant="outlined" startIcon={<GavelRounded />} endIcon={<OpenInNewRounded />} onClick={() => openTool('drafting')}>Open controlled drafting</Button>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">Stage copilot prompts</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 1.2 }}>The copilot receives this matter context; the prompt still requires source verification and human judgment.</Typography>
                <Stack spacing={0.7}>{selectedStage?.prompts.map(prompt => <Button key={prompt} variant="text" endIcon={<ArrowForwardRounded />} onClick={() => openTool('copilot', `${prompt} for ${playbook.matter.title}. Use only the verified matter record and connected case-law corpus. Clearly identify missing facts, assumptions, adverse authority, jurisdiction or scope limits, and decisions requiring responsible-professional approval.`)} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>{prompt}</Button>)}</Stack>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <Stack spacing={2} sx={{ position: { xl: 'sticky' }, top: { xl: 0 }, alignSelf: 'start' }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}><Typography variant="h6">Next best actions</Typography><Chip size="small" label="Prioritized" color="secondary" variant="outlined" /></Stack>
            <Stack spacing={0.4}>
              {playbook?.nextActions.slice(0, 5).map((task, index) => (
                <Box key={task.id} component="button" type="button" onClick={() => chooseStage(task.stage, true)} sx={{ appearance: 'none', border: 0, width: '100%', bgcolor: 'transparent', textAlign: 'left', p: 1, borderRadius: 2, cursor: 'pointer', color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start"><Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: index === 0 ? 'primary.main' : 'action.selected', color: index === 0 ? '#fff' : 'text.secondary' }}>{index + 1}</Avatar><Box><Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>{task.title}</Typography><Typography variant="caption" color={task.dueState === 'overdue' || task.status === 'blocked' ? 'error.main' : 'text.secondary'}>{task.reason}</Typography></Box></Stack>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Risk & quality controls</Typography>
            <Stack spacing={1.1} sx={{ mt: 1.3 }}>
              {playbook?.risks.length ? playbook.risks.slice(0, 5).map(risk => (
                <Box key={risk.code} sx={{ p: 1.2, borderRadius: 2, bgcolor: risk.severity === 'critical' ? 'rgba(194,65,75,.07)' : risk.severity === 'warning' ? 'rgba(217,119,6,.07)' : 'action.hover', borderLeft: '3px solid', borderColor: risk.severity === 'critical' ? 'error.main' : risk.severity === 'warning' ? 'warning.main' : 'info.main' }}>
                  <Typography variant="body2" sx={{ fontWeight: 730 }}>{risk.label}</Typography><Typography variant="caption" color="text.secondary">{risk.detail}</Typography>
                </Box>
              )) : <Alert severity="success" icon={<CheckCircleRounded />}>No automated workflow exceptions are open.</Alert>}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Connected workspace</Typography>
            <Typography variant="caption" color="text.secondary">Open the same matter in the tool needed for this stage.</Typography>
            <Stack spacing={0.7} sx={{ mt: 1.2 }}>
              {(selectedStage?.tools || []).map(toolId => {
                const tool = TOOL_META[toolId];
                if (!tool) return null;
                return <Button key={toolId} variant="text" onClick={() => openTool(toolId)} startIcon={tool.icon} endIcon={<ArrowForwardRounded />} sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 0.8, '& .MuiButton-endIcon': { ml: 'auto' } }}><Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 720 }}>{tool.label}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{tool.description}</Typography></Box></Button>;
              })}
            </Stack>
          </Paper>

          <Alert severity="info" icon={<LockRounded />} sx={{ alignItems: 'flex-start' }}>{playbook?.disclaimer}</Alert>
        </Stack>
      </Box>

      <TaskDialog open={Boolean(taskEditor)} value={taskEditor || {}} setValue={setTaskEditor} onClose={() => setTaskEditor(null)} onSave={saveTask} busy={busy} />
      <ApprovalDialog task={approvalTask} value={approval} setValue={setApproval} onClose={() => setApprovalTask(null)} onSave={saveApproval} busy={busy} />
      <Snackbar open={Boolean(snackbar)} autoHideDuration={4000} onClose={() => setSnackbar('')} message={snackbar} />
      {busy && <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(8,26,42,.06)', zIndex: theme => theme.zIndex.modal - 1, pointerEvents: 'none' }} />}
    </Box>
  );
}
